import React, { useState } from 'react'
import { ErrorOutline } from '@mui/icons-material'
import InputModal from '../../../../components/modals/common/InputModal'
import Button from '../../../../components/ui/Button'
import {
	getFileRenderingError,
	PASSWORD_PROTECTED_FILE_TITLE,
	PASSWORD_PROTECTED_FILE_DESCRIPTION,
	PASSWORD_PROTECTED_FILE_MESSAGE,
} from '../../../../hooks/file/previewErrors'

interface PasswordProtectedFileProps {
	message: string
	onUnlock: (password: string) => Promise<string | null>
}

const PasswordProtectedFile: React.FC<PasswordProtectedFileProps> = ({
	message,
	onUnlock,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [isChecking, setIsChecking] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const handleUnlock = async (password: string) => {
		if (isChecking) return
		setIsChecking(true)
		setError(null)
		try {
			const message = await onUnlock(password)
			if (message) setError(message)
			else setIsOpen(false)
		} catch (err) {
			setError(getFileRenderingError(err))
		} finally {
			setIsChecking(false)
		}
	}

	return (
		<div className="flex max-w-lg flex-col items-center gap-3 text-center text-text-main dark:text-text-main-dark">
			<div className="flex items-center justify-center gap-2 font-medium">
				<ErrorOutline className="shrink-0 fill-negative" />
				<span>{PASSWORD_PROTECTED_FILE_TITLE}</span>
			</div>
			<p className="text-sm font-normal">
				{message === PASSWORD_PROTECTED_FILE_MESSAGE
					? PASSWORD_PROTECTED_FILE_DESCRIPTION
					: message}
			</p>
			<Button
				label="Enter password"
				onClick={() => {
					setError(null)
					setIsOpen(true)
				}}
			/>
			{isOpen && (
				<InputModal
					isOpen={isOpen}
					onClose={() => {
						if (!isChecking) setIsOpen(false)
					}}
					title="Enter Password"
					inputLabel=""
					inputPlaceholder="Password"
					actionLabel="Open preview"
					loading={isChecking}
					inputProps={{
						hideValue: true,
						'aria-label': 'Password',
						autoComplete: 'off',
						autoCapitalize: 'none',
						spellCheck: false,
						errorClassName: 'text-left',
						error,
						onKeyDown: (event) => {
							if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
							event.preventDefault()
							const password = event.currentTarget.value.trim()
							if (password) void handleUnlock(password)
						},
					}}
					onAction={handleUnlock}
				/>
			)}
		</div>
	)
}

export default PasswordProtectedFile
